import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { FC, ReactNode } from 'react';
import { useAuth as useCoreAuth } from 'app/auth/contexts/AuthContext';
import { useUser } from 'app/auth/contexts/UserContext';
import { navigationRef } from 'app/services/NavigationService';
import { Telemetry } from 'app/services/telemetry/Telemetry';
import { serializeSnakeToCamel } from '@shared/utils';
import {
  AUTO_START_POLL_INTERVAL,
  TOUR_START_STABLE_TICKS,
  TOUR_WAIT_MAX_ATTEMPTS,
} from '../constants';
import { useRecordTour } from '../hooks/useRecordTour';
import { useTourCatalogue } from '../hooks/useTourCatalogue';
import type {
  TTour,
  TTourCompletionStatus,
  TTourId,
  TTourResponse,
  TTourStep,
} from '../types';
import { getAutoStartTour, getPlayableSteps, getReplayableTours, isTourOnRoute, isTourVisibleForRole, toTourRegistry, traceTour } from '../utils';

type TOnboardingContext = {
  activeTour: TTour | null;
  steps: TTourStep[];
  replayableTours: TTour[];
  endTour: (status: TTourCompletionStatus) => void;
  replayTour: (tourId: TTourId) => void;
};

const OnboardingContext = createContext<TOnboardingContext | undefined>(undefined);

const ACTING_ROLE = 'patient';

const getLiveRoute = (): string | null =>
  navigationRef.isReady() ? (navigationRef.getCurrentRoute()?.name ?? null) : null;

export const OnboardingProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useCoreAuth();
  const { meData } = useUser();

  const [routeName, setRouteName] = useState<string | null>(null);
  const [activeTour, setActiveTour] = useState<TTour | null>(null);
  const [steps, setSteps] = useState<TTourStep[]>([]);
  const [endedTourIds, setEndedTourIds] = useState<TTourId[]>([]);

  const role = isAuthenticated ? ACTING_ROLE : null;

  const pendingTours = useMemo(() => {
    const registry = toTourRegistry(serializeSnakeToCamel<TTourResponse[]>(meData?.onboarding));

    traceTour(
      'me.onboarding:',
      meData ? (meData.onboarding ?? 'absent from the payload') : 'me has not loaded yet',
      '→ pending:',
      Object.keys(registry).length ? Object.keys(registry) : 'none',
    );

    return registry;
  }, [meData]);

  const catalogue = useTourCatalogue(isAuthenticated);
  const recordTour = useRecordTour();

  useEffect(() => {
    const update = () => setRouteName(getLiveRoute());

    update();

    return navigationRef.addListener('state', update);
  }, []);

  const startTour = useCallback(
    (tour: TTour) => {
      if (!isTourVisibleForRole(tour, role)) {
        traceTour(tour.id, 'is not for', role, '— roles:', tour.roles, 'enabled:', tour.enabled);
        return;
      }

      const playable = getPlayableSteps(tour, role);

      if (!playable.length) {
        traceTour(
          tour.id,
          'has no playable step: none of its anchors are mounted —',
          tour.steps.map(step => step.target),
        );
        return;
      }

      if (playable.length < tour.steps.length) {
        traceTour(
          tour.id,
          `playing ${playable.length} of ${tour.steps.length} steps, dropped:`,
          tour.steps.filter(step => !playable.includes(step)).map(step => step.target),
        );
      }

      setSteps(playable);
      setActiveTour(tour);
    },
    [role],
  );

  const endTour = useCallback(
    (status: TTourCompletionStatus) => {
      if (!activeTour) {
        return;
      }

      setEndedTourIds(current => [...current, activeTour.id]);
      setActiveTour(null);
      setSteps([]);

      Telemetry.event('ui.onboarding.tour_ended', {
        domain: 'ui',
        data: { tour: activeTour.id, version: activeTour.version, status },
      });

      recordTour({ tour: activeTour.id, version: activeTour.version, status });
    },
    [activeTour, recordTour],
  );

  const replayTour = useCallback(
    (tourId: TTourId) => {
      const tour = catalogue[tourId] ?? pendingTours[tourId];

      if (tour) {
        startTour(tour);
      }
    },
    [catalogue, pendingTours, startTour],
  );

  useEffect(() => {
    if (!activeTour) {
      return;
    }

    const liveRoute = getLiveRoute();

    if (isTourOnRoute(activeTour, liveRoute)) {
      return;
    }

    traceTour('dropping', activeTour.id, ': route is', liveRoute, 'not', activeTour.routes);

    endTour('skipped');
  }, [activeTour, routeName, endTour]);

  const startableTours = useMemo(
    () => Object.values(pendingTours).filter(tour => !endedTourIds.includes(tour.id)),
    [pendingTours, endedTourIds],
  );

  useEffect(() => {
    if (activeTour || !meData || !role) {
      return;
    }

    traceTour(
      'waiting for a tour to start, startable:',
      startableTours.length ? startableTours.map(tour => tour.id) : 'none',
    );

    let attempts = 0;
    let playableBefore = 0;
    let stableTicks = 0;

    const interval = setInterval(() => {
      attempts += 1;

      const liveRoute = getLiveRoute();
      const candidate = getAutoStartTour(startableTours, role, liveRoute);
      const playable = candidate ? getPlayableSteps(candidate, role).length : 0;

      stableTicks = playable > 0 && playable === playableBefore ? stableTicks + 1 : 0;
      playableBefore = playable;

      if (candidate && stableTicks >= TOUR_START_STABLE_TICKS) {
        traceTour('starting', candidate.id, 'on', liveRoute, 'with', playable, 'steps');
        clearInterval(interval);
        startTour(candidate);
      } else if (attempts >= TOUR_WAIT_MAX_ATTEMPTS) {
        traceTour(
          'gave up after',
          attempts,
          'attempts — candidate:',
          candidate?.id ?? 'none',
          'route:',
          liveRoute,
          'playable steps:',
          playable,
        );
        clearInterval(interval);
      }
    }, AUTO_START_POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [activeTour, meData, role, routeName, startableTours, startTour]);

  const replayableTours = useMemo(
    () => getReplayableTours(Object.keys(catalogue).length ? catalogue : pendingTours, role),
    [catalogue, pendingTours, role],
  );

  const contextValue = useMemo(
    () => ({ activeTour, steps, replayableTours, endTour, replayTour }),
    [activeTour, steps, replayableTours, endTour, replayTour],
  );

  return <OnboardingContext.Provider value={contextValue}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = (): TOnboardingContext => {
  const context = useContext(OnboardingContext);

  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }

  return context;
};
